import React, { useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Add, Delete, Warning } from "@mui/icons-material";
import { TherapyType } from "../../../api-client";
import {
  useCreateCatalogTerm,
  useUpdateCatalogTerm,
} from "../../../stores/MasterTables/therapies.mutations";
import ColorField, { isValidHexColor } from "./ColorField";

/**
 * Create/edit a prescribed TERM together with its aliases and resolutions.
 *
 * Aliases and resolutions are sent as complete lists, not deltas — the server
 * replaces what it stores with what this dialog shows. That keeps the mental
 * model simple ("what I see is what will be stored") and avoids a half-applied
 * state if one of several delta calls fails.
 */

interface Props {
  open: boolean;
  term: any | null;
  methods: any[];
  /** All terms, used only to offer the colours already in use as a palette. */
  terms?: any[];
  therapyType: TherapyType;
  onClose: () => void;
}

interface ResolutionRow {
  therapyMethodId: string;
  priority: number;
}

const ModalTerm: React.FC<Props> = ({ open, term, methods, terms = [], therapyType, onClose }) => {
  const isEdit = !!term;
  const createTerm = useCreateCatalogTerm();
  const updateTerm = useUpdateCatalogTerm();

  const [label, setLabel] = useState<string>(term?.label ?? "");
  const [color, setColor] = useState<string | null>(term?.color ?? null);
  const [isSupplementary, setIsSupplementary] = useState<boolean>(term?.isSupplementary ?? false);
  const [type, setType] = useState<string>(term?.therapyType ?? "");
  const [aliases, setAliases] = useState<string[]>(
    (term?.aliases ?? []).map((a: any) => a.alias)
  );
  const [resolutions, setResolutions] = useState<ResolutionRow[]>(
    (term?.resolutions ?? []).map((r: any) => ({
      therapyMethodId: r.therapyMethodId,
      priority: r.priority,
    }))
  );

  const methodById = useMemo(
    () => new Map((methods ?? []).map((m: any) => [m.id, m])),
    [methods]
  );

  const busy = createTerm.isPending || updateTerm.isPending;

  const addResolution = () =>
    setResolutions((prev) => [
      ...prev,
      // 100 is the canonical first choice; each further row steps down so a new
      // list is already meaningfully ordered without the admin doing arithmetic
      { therapyMethodId: "", priority: prev.length === 0 ? 100 : Math.max(0, 100 - prev.length * 10) },
    ]);

  const setResolution = (idx: number, patch: Partial<ResolutionRow>) =>
    setResolutions((prev) => prev.map((r, i) => (i === idx ? { ...r, ...patch } : r)));

  const removeResolution = (idx: number) =>
    setResolutions((prev) => prev.filter((_, i) => i !== idx));

  const duplicateMethods = useMemo(() => {
    const seen = new Set<string>();
    const dupes = new Set<string>();
    resolutions.forEach((r) => {
      if (!r.therapyMethodId) return;
      if (seen.has(r.therapyMethodId)) dupes.add(r.therapyMethodId);
      seen.add(r.therapyMethodId);
    });
    return dupes;
  }, [resolutions]);

  /**
   * A term that resolves to exactly one method should carry that method's
   * colour: the slot is drawn from the TERM while unscheduled and from the
   * METHOD once scheduled, so a mismatch makes the same treatment visibly
   * change colour the moment it is planned. With several methods there is no
   * single colour to match, so the check only applies to the unambiguous case.
   */
  const colorMismatch = useMemo(() => {
    if (!color || resolutions.length !== 1) return null;
    const method: any = methodById.get(resolutions[0].therapyMethodId);
    if (!method?.color) return null;
    return method.color.toLowerCase() === color.toLowerCase() ? null : method;
  }, [color, resolutions, methodById]);

  const problems = useMemo(() => {
    const list: string[] = [];
    if (!label.trim()) list.push("Bezeichnung fehlt");
    if (color && !isValidHexColor(color)) list.push("Farbe ist kein gültiger Hex-Wert");
    if (duplicateMethods.size) list.push(`Methode mehrfach: ${[...duplicateMethods].join(", ")}`);
    if (resolutions.some((r) => !r.therapyMethodId)) list.push("Eine Auflösung ohne Methode");
    if (!aliases.length) list.push("Ohne Schreibweise wird der Begriff nie erkannt");
    if (!resolutions.length) list.push("Ohne Methode gibt es nichts abzurechnen");
    if (colorMismatch)
      list.push(
        `Ohne gleiche Farbe wie ${colorMismatch.id} (${colorMismatch.color}) wechselt der Termin ` +
          `beim Verplanen die Farbe`
      );
    return list;
  }, [label, color, aliases, resolutions, duplicateMethods, colorMismatch]);

  const blocking = problems.filter(
    (p) => !p.startsWith("Ohne ") // the "Ohne …" ones are warnings, not blockers
  );

  const handleSave = async () => {
    const payload = {
      label: label.trim(),
      // Always sent, so clearing the swatch actually clears the stored colour.
      color,
      isSupplementary,
      therapyType: (type || null) as any,
      aliases,
      resolutions: resolutions
        .filter((r) => r.therapyMethodId)
        .map((r) => ({ therapyMethodId: r.therapyMethodId, priority: Number(r.priority) || 0 })),
    };
    try {
      if (isEdit) {
        await updateTerm.mutateAsync({ id: term.id, data: payload as any });
      } else {
        await createTerm.mutateAsync(payload as any);
      }
      onClose();
    } catch {
      // notification surfaced by the mutation; keep the dialog open so the
      // admin can correct the alias clash the server just reported
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {isEdit ? `Begriff bearbeiten — ${term.id}` : "Neuen Begriff anlegen"}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Bezeichnung"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            fullWidth
            required
            helperText={
              isEdit
                ? "Die ID bleibt unverändert."
                : "Die ID wird aus der Bezeichnung abgeleitet, z. B. „KG-ZNS nach Bobath“ → KG_ZNS_NACH_BOBATH"
            }
          />

          <Stack direction="row" spacing={2}>
            <TextField
              select
              label="Disziplin"
              value={type}
              onChange={(e) => setType(e.target.value)}
              sx={{ minWidth: 220 }}
              helperText="Leer = gilt für alle"
            >
              <MenuItem value="">alle</MenuItem>
              <MenuItem value={TherapyType.Physiotherapie}>Physiotherapie</MenuItem>
              <MenuItem value={TherapyType.Ergotherapie}>Ergotherapie</MenuItem>
            </TextField>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isSupplementary}
                  onChange={(e) => setIsSupplementary(e.target.checked)}
                />
              }
              label="Zuschlag (kein eigenständiges Heilmittel)"
            />
          </Stack>

          <ColorField
            value={color}
            onChange={setColor}
            suggestions={[
              ...(terms ?? []).map((t: any) => t.color),
              ...(methods ?? []).map((m: any) => m.color),
            ].filter(Boolean)}
            helperText="Farbe eines noch nicht verplanten Termins. Leer = Standardfarbe des Clients."
          />

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Schreibweisen
            </Typography>
            <Autocomplete
              multiple
              freeSolo
              options={[]}
              value={aliases}
              onChange={(_, v) => setAliases(v as string[])}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip size="small" label={option} {...getTagProps({ index })} key={option} />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="z. B. KG-ZNS, KGZNS, kg zns — Enter zum Hinzufügen"
                  helperText="Groß-/Kleinschreibung, Bindestriche und Umlaute werden beim Vergleich ignoriert; Varianten, die dabei gleich werden, zählen als eine."
                />
              )}
            />
          </Box>

          <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography variant="subtitle2">Auflösung auf Methoden</Typography>
              <Button size="small" startIcon={<Add />} onClick={addResolution}>
                Methode hinzufügen
              </Button>
            </Stack>
            <Typography variant="caption" color="text.secondary">
              Höhere Priorität gewinnt. Wählt der Therapeut nicht selbst, wird die höchste Methode
              genommen, für die er qualifiziert ist.
            </Typography>

            <Stack spacing={1} sx={{ mt: 1 }}>
              {resolutions.map((r, idx) => {
                const method: any = methodById.get(r.therapyMethodId);
                const deadEnd = method && (method.lineItems ?? []).length === 0;
                return (
                  <Stack key={idx} direction="row" spacing={1} alignItems="center">
                    <TextField
                      select
                      label="Methode"
                      value={r.therapyMethodId}
                      onChange={(e) => setResolution(idx, { therapyMethodId: e.target.value })}
                      sx={{ minWidth: 320 }}
                      size="small"
                      error={duplicateMethods.has(r.therapyMethodId)}
                    >
                      {(methods ?? []).map((m: any) => (
                        <MenuItem key={m.id} value={m.id}>
                          {m.id} — {m.label}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      label="Priorität"
                      type="number"
                      size="small"
                      value={r.priority}
                      onChange={(e) => setResolution(idx, { priority: Number(e.target.value) })}
                      sx={{ width: 120 }}
                    />
                    {deadEnd && (
                      <Tooltip title="Diese Methode hat keine abrechenbare Zeile — die Auflösung läuft ins Leere">
                        <Warning color="warning" fontSize="small" />
                      </Tooltip>
                    )}
                    {method && (method.lineItems ?? []).length > 0 && (
                      <Typography variant="caption" color="text.secondary">
                        {method.lineItems.length} Abrechnungszeile(n)
                      </Typography>
                    )}
                    <Box sx={{ flex: 1 }} />
                    <IconButton size="small" color="error" onClick={() => removeResolution(idx)}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </Stack>
                );
              })}
            </Stack>
          </Box>

          {problems.length > 0 && (
            <Alert severity={blocking.length ? "error" : "warning"}>
              <Stack spacing={0.5}>
                {problems.map((p) => (
                  <Typography key={p} variant="body2">
                    {p}
                  </Typography>
                ))}
              </Stack>
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Abbrechen
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={busy || blocking.length > 0}
        >
          {busy ? "Speichert…" : "Speichern"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModalTerm;
